import { QueueAccessType } from 'app/modules/queues/models';

/**
 * Sorts workflow action triggers by the queue's `workflow_order` array.
 * Workflows present in the ordering are sorted by their position;
 * workflows not in the ordering are appended alphabetically at the end.
 * Falls back to purely alphabetical sort when no ordering is provided.
 */
export const sortByWorkflowOrder = <T extends { id: number; label: string }>(
  items: T[],
  workflowOrder: number[] | null | undefined,
): T[] => {
  if (!workflowOrder?.length) {
    return items.toSorted((a, b) =>
      a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
    );
  }
  const orderMap = new Map(workflowOrder.map((id, idx) => [id, idx]));
  return items.toSorted((a, b) => {
    const aPos = orderMap.get(a.id) ?? Infinity;
    const bPos = orderMap.get(b.id) ?? Infinity;
    if (aPos === bPos) {
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    }
    return aPos - bPos;
  });
};

export const isQueueAccessible = (
  hasReadAssignmentsPermission: boolean,
  accessibleQueues: number[],
  queueId: number | null,
  queueAccessType?: `${QueueAccessType}`,
): boolean => {
  // If agent is an admin, all queues are accessible to them.
  if (hasReadAssignmentsPermission) {
    return true;
  }
  // If `queueAccessType` is undefined (object is not an article and not subject to queue access)
  // or `OPEN` (article is in an open queue accessible to all agents), return true.
  if (
    queueAccessType === undefined ||
    queueAccessType === QueueAccessType.OPEN
  ) {
    return true;
  }
  // If `queueId` is undefined (article with no queue is assumed to be in the Default Queue)
  // or included in `accessibleQueues` (article is in a queue that agent has access to), return true.
  if (queueId === null || accessibleQueues.includes(queueId)) {
    return true;
  }

  return false;
};
